import clsx from 'clsx'
import isArray from 'lodash/isArray'
import map from 'lodash/map'
import React, { FC, Fragment, useCallback } from 'react'

import SvgCheck from '@Assets/SVG/Check'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'

import styles from './BubbleNavigation.module.scss'

type NavigationStepKey = string
export interface NavigationStep<T> {
  key: T
  title: string | string[]
}
export interface BubbleNavigationProps<T> {
  activeStep: NavigationStep<T>
  className?: string
  onClick: (step: NavigationStep<T>) => void
  stepValidations: Record<NavigationStepKey, boolean>
  steps: NavigationStep<T>[]
}

export const BubbleNavigation: FC<BubbleNavigationProps<string>> = ({ activeStep, className, onClick, stepValidations, steps }) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t } = useTranslation()
  const isPrevActive = useCallback((index: number) => steps.findIndex(step => activeStep.key === (step.key)) < index, [activeStep, steps])
  const isActive = useCallback(step => activeStep.key === step.key, [activeStep])
  const isStepValid = useCallback(
    (step, index) => !isActive(step) && !isPrevActive(index) && stepValidations[step.key],
    [isActive, isPrevActive, stepValidations],
  )
  const getBubbleContext = useCallback((step, index) => {
    if (!cofDesignV1 && isStepValid(step, index)) {
      return <SvgCheck />
    }
    return (
      <div className={styles.number}>
        {index + 1}
      </div>
    )
  }, [cofDesignV1, isStepValid])

  return (
    <div className={clsx(styles.bubbleNavigation, cofDesignV1 && styles.cofDesignV1, className)}>
      {map(steps, (step, index) => (
        <Fragment key={`step-${step.key}`}>
          <button
            className={clsx(
              styles.step,
              isActive(step) && styles.active,
              isStepValid(step, index) && styles.valid,
            )}
            onClick={() => onClick(step)}
            type="button"
          >
            {getBubbleContext(step, index)}
            <div className={styles.name}>
              {isArray(step.title) ? step.title.map(a => t(a)).join(' & ') : t(step.title, step.title)}
            </div>
          </button>
          {index < steps.length - 1 && (
            <div key={`line-${step}`} className={clsx(styles.line, !isActive(step) && !isPrevActive(index) && styles.valid)} />
          )}
        </Fragment>
      ))}
    </div>
  )
}

